import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Icon } from "../ui/Icon";

/**
 * Promise-based confirmation dialog (replaces the legacy `confirm()`).
 * `useConfirm()` returns a function that resolves to true/false. Built from the
 * shared dialog and button primitives — it has no styles of its own.
 */

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOptions(null);
  }, []);

  useEffect(() => {
    if (!options) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [options, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <div
          className="dialog-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={options.title}
          onClick={() => close(false)}
        >
          <div className="dialog dialog-sm dialog-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-confirm-body">
              <div className="dialog-confirm-title">
                {/* The canvas leads a destructive confirmation with a tinted
                    mark, so the consequence is legible before the sentence is
                    read. */}
                <span
                  className={`dialog-confirm-mark${options.variant === "danger" ? " is-danger" : ""}`}
                >
                  <Icon name="alertCircle" size={16} />
                </span>
                <h2>{options.title}</h2>
              </div>
              {options.body && <p>{options.body}</p>}
            </div>
            <div className="dialog-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => close(false)}
              >
                {options.cancelLabel ?? "Cancel"}
              </button>
              <button
                type="button"
                className={`btn ${options.variant === "danger" ? "btn-danger" : "btn-primary"}`}
                onClick={() => close(true)}
                autoFocus
              >
                {options.confirmLabel ?? "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return ctx;
}
